import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can, PRIORITY, TASK_STATUS } from "@/lib/colors";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { todayStr } from "@/lib/dates";

const blank = (assignee, date, project) => ({ title: "", description: "", assignee_id: assignee, date: date || todayStr(), time: "17:00", priority: "medium", status: "todo", project_id: project || "none" });

const Field = ({ label, children }) => (
  <div className="space-y-1.5"><Label className="text-slate-600">{label}</Label>{children}</div>
);

const Choice = ({ value, onChange, options, testid }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testid}><SelectValue /></SelectTrigger>
    <SelectContent>{options.map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
  </Select>
);

export const TaskDialog = ({ open, task, presetAssignee, presetDate, presetProject, onClose, onSaved }) => {
  const { user } = useAuth();
  const lead = can(user, "assign_tasks");
  const [form, setForm] = useState(blank(user.id));
  const [people, setPeople] = useState([]);
  const [projects, setProjects] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(task ? { ...task, project_id: task.project_id || "none", date: task.due ? task.due.slice(0, 10) : "", time: task.due ? task.due.slice(11, 16) : "17:00" } : blank(lead && presetAssignee ? presetAssignee : user.id, presetDate, presetProject));
    api.get("/projects").then((r) => setProjects(r.data));
    if (lead) api.get("/users").then((r) => setPeople(r.data.filter((u) => u.id === user.id || (u.role !== "admin" && (user.manages || []).includes(u.role)))));
  }, [open, task, presetAssignee, presetDate, presetProject, lead, user.id, user.manages]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

  const save = async () => {
    if (!form.title.trim()) return toast.error("Please add a title");
    setBusy(true);
    const body = { title: form.title, description: form.description, assignee_id: form.assignee_id, due: form.date ? `${form.date}T${form.time || "17:00"}` : null, priority: form.priority, status: form.status, project_id: form.project_id === "none" ? (task ? "" : null) : form.project_id };
    try {
      if (task) await api.patch(`/tasks/${task.id}`, body);
      else await api.post("/tasks", body);
      toast.success(task ? "Task updated" : "Task created");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const assignees = lead ? people.map((p) => [p.id, p.id === user.id ? `${p.name} (me)` : `${p.name}${p.department ? ` · ${p.department}` : ""}`]) : [[user.id, `${user.name} (me)`]];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="task-dialog">
        <DialogHeader><DialogTitle className="text-2xl text-[#24474A]">{task ? "Edit task" : "New task"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <Field label="Title"><Input value={form.title} onChange={set("title")} data-testid="task-title-input" /></Field>
          <Field label="Description"><Textarea rows={3} value={form.description} onChange={set("description")} data-testid="task-description-input" /></Field>
          <Field label="Assignee">
            {assignees.length > 0 && <Choice value={form.assignee_id} onChange={set("assignee_id")} options={assignees} testid="task-assignee-select" />}
          </Field>
          <Field label="Project / Location">
            <Choice value={form.project_id || "none"} onChange={set("project_id")} testid="task-project-select"
              options={[["none", "None"], ...projects.filter((p) => !p.archived || p.id === form.project_id).sort((a, b) => (a.kind === "location") - (b.kind === "location")).map((p) => [p.id, p.kind === "location" ? `Location · ${p.name}` : p.name])]} />
          </Field>
<div className="grid grid-cols-2 gap-3">
            <Field label="Due date"><Input type="date" value={form.date} onChange={set("date")} data-testid="task-due-date-input" /></Field>
            <Field label="Due time"><Input type="time" value={form.time} onChange={set("time")} data-testid="task-due-time-input" /></Field>
            <Field label="Priority"><Choice value={form.priority} onChange={set("priority")} options={Object.entries(PRIORITY).map(([k, v]) => [k, v.label])} testid="task-priority-select" /></Field>
            <Field label="Status"><Choice value={form.status} onChange={set("status")} options={Object.entries(TASK_STATUS).map(([k, v]) => [k, v.label])} testid="task-status-select" /></Field>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" className="rounded-full" onClick={onClose}>Cancel</Button>
          <Button className="rounded-full" onClick={save} disabled={busy} data-testid="task-save-btn">{task ? "Save task" : "Create task"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
